import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import {useHistory} from 'react-router-dom';
import {Motion, spring} from 'react-motion';
import {connect} from 'react-redux';
import SearchFieldComponent from '../components/common/SearchFieldComponent';
import CustomerNameTableCell from '../components/customer/CustomerNameTableCell';
import TableCell from '../components/table/TableCell';
import {
    filterCustomers,
    groupAndSortItemsByFieldCharacter,
    sortItemByField
} from '../utils/customersUtils';
import Pagination from '../components/common/Pagination';
import LinkTableCellComponent from '../components/common/LinkTableCellComponent';
import {CONTRACTS, getAllContracts} from '../redux/actions/contractsActions';
import {CUSTOMERS, fetchAllCustomer} from '../redux/actions/CustomersActions';
import tableHeadings from '../data/tableHeadings/fulfillmentCustomerTableHeading';
import ContractLinksTableCell from '../components/customer/ContractLinksTableCell';
import TableHeadingComponent from '../components/common/TableHeadingComponent';
import TableRowGroupsComponent from '../components/common/TableRowGroupsComponent';

import {
    PageWrapper,
    TableWrapper,
    PageHeadingWrapper,
    PageContentWrapper,
    ColumnHeading,
    PaginationWrapper
} from '../styles/PageStyles';
import {isValidNumber} from '../utils/formatter';
import {profileSections} from './CustomerProfile';
import {cancelRequest} from '../redux/actions/ApiCallsActions';

function FulfillmentCustomers(props) {
    const history = useHistory();
    const {company, customers, fetchAllCustomer, getAllContracts, cancelRequest} = props;
    //customers = customerData;
    useEffect(() => {
        fetchAllCustomer();
        getAllContracts();
        return () => {
            // Clean up request
            console.log('customers unmount');
            cancelRequest(CUSTOMERS);
        };
    }, []);

    const [isUploading, setIsUploading] = useState(false);
    const [selectedCustomers, setSelectedCustomer] = useState([]);
    const [customerQuery, setCustomerQuery] = useState('');

    const [paginationPage, setPaginationPage] = useState(0);
    const [paginationRowsPerPage, setPaginationRowsPerPage] = useState(10);

    const handleOnSearchChange = e => {
        setCustomerQuery(e.target.value);
    };

    const handleOnCustomerNameClick = customer => {
        history.push(`/fulfillment/customers/${customer.companyId}/${customer.id}`, { section: profileSections.Identity });
    };

    const handleOnContractLinkClick = customer => {
        history.push(`/fulfillment/customers/${customer.companyId}/${customer.id}`, { section: profileSections.Contracts });
    };

    const handleOnPolicesClick = customer => {
        history.push(`/fulfillment/customers/${customer.companyId}/${customer.id}`, { section: profileSections.Devices });
    };

    const handleOnPageChange = page => {
        setPaginationPage(page);
    };

    const handleOnRowsPerPageChange = rowsPerPage => {
        setPaginationRowsPerPage(rowsPerPage);
        setPaginationPage(0);
    };

    //TODO FIX logic.
    const onCustomerCheckboxSelected = (e, customer) => {
        //let checked = e.target.checked;
        // we're replacing the previous customer, otherwise, we're setting the selected customer back to null
        //onCustomerSelected(checked ? customer : null);

        // if (customer && customer.warning) return; // when customer has warning they can't be selected.
        // setSelectedCustomer(checked ? customer : null);

        if (selectedCustomers.includes(customer.id)) {
            setSelectedCustomer([]);
        } else {
            setSelectedCustomer([
                customer.id
            ]);
        }
    };
    /**
     *  Prepare the table cell data to be handed into the table Customers table.
     *
     *  The table cells are custom component. We create the table cell here
     *  so that we can handle any logic attached a table cell outside of the component
     */
    const addTableFieldsToCustomerData = (customerData, selectedCustomer) => customerData.map(customer => {
        const mode = (selectedCustomers.includes(customer.id)) ? 'selected' : (selectedCustomers.length) ? 'disabled' : 'default';

        let policies = 0;
        if (customer.contracts) {
            customer.contracts.forEach(contract => {
                if (isValidNumber(contract.policies)) {
                    policies += contract.policies;
                }
            });
        }

        return {
            ...customer,
            /**
                     *  NB - the table cell need to have a key because they are going to used in a list.
                     *  NB - the keys for `tableField` should map back to a key from the header data provided.
                     */
            tableFields: {
                id: customer.id,
                name: <CustomerNameTableCell
                    key={1}
                    customerName={customer.name}
                    mode={mode}
                    onCustomerNameClick={() => handleOnCustomerNameClick(customer)}
                    loading={customer.syncing}
                    warning={customer.warning}
                />,
                email: <TableCell key={2}>{customer.email}</TableCell>,
                company: <TableCell key={3}>{customer.company}</TableCell>,
                contractId: <ContractLinksTableCell key={4} onClick={() => handleOnContractLinkClick(customer)} contracts={customer.contracts}/>,
                policies: <LinkTableCellComponent
                    key={5}
                    name={policies}
                    onLinkClick={() => handleOnPolicesClick(customer)}
                />
            }
        };
    });

    // Add table cell components
    const customersWithTableFields = addTableFieldsToCustomerData(customers, selectedCustomers);

    // Apply search filter
    let filteredCustomer = [...customersWithTableFields];
    if (customerQuery) filteredCustomer = filterCustomers(customerQuery, filteredCustomer);

    const sortedCustomer = sortItemByField(filteredCustomer, 'companyName');

    const start = paginationPage * paginationRowsPerPage;
    const end = (paginationPage + 1) * paginationRowsPerPage;
    const paginatedCustomerData = sortedCustomer.slice(start, end);

    // Order the table data.
    const orderedCustomerData = groupAndSortItemsByFieldCharacter(paginatedCustomerData, 'companyName');

    // Pagination functions and variables
    const paginationCount = filteredCustomer.length;

    return (
        <PageWrapper>

            <PageHeadingWrapper>
                Customer
            </PageHeadingWrapper>

            <div style={{margin: '8px 40px'}}>
                <SearchFieldComponent
                    onSearchChange={handleOnSearchChange}
                    placeholder="Filter customer by name, email or company"
                />
            </div>

            {/**
             *  Customers Table
             **/}
            <TableWrapper>
                <TableHeadingComponent
                    tableHeadings={tableHeadings}
                    hasCheckBox={false}
                />

                {
                    Object.keys(orderedCustomerData).map(key => (
                        <TableRowGroupsComponent
                            key={key}
                            tableHeadings={tableHeadings}
                            groupHeading={key}
                            rows={orderedCustomerData[key].map(data => data.tableFields)}
                            selectedRows={selectedCustomers}
                            hasCheckBoxes={true}
                            onCheckBoxSelected={onCustomerCheckboxSelected}
                        />
                    ))
                }
            </TableWrapper>

            {/**
             * Pagination Section
             **/}
            <Motion
                defaultStyle={{y: 200}}
                style={{y: spring(paginationCount >= 1 ? 0 : 200)}}
            >
                {
                    style => (
                        <PaginationWrapper
                            style={{transform: `translateY(${style.y}px)`, }}
                        >
                            <Pagination
                                count={paginationCount}
                                page={paginationPage}
                                rowsPerPage={paginationRowsPerPage}
                                onChangePage={handleOnPageChange}
                                onRowsPerPageChange={handleOnRowsPerPageChange}
                            />
                        </PaginationWrapper>
                    )}
            </Motion>

        </PageWrapper>
    );
}

FulfillmentCustomers.propTypes = {};
FulfillmentCustomers.defaultProps = {};

const mapStateToProps = state => {
    let {customers} = state;
    const {contracts} = state;

    if (customers.length) {
        // group the customers to contracts
        customers = customers.map(customer => {
            const customerContracts = contracts.filter(contract => customer.representativeId === contract.representativeId);
            return {
                ...customer,
                name: customer.representativeName,
                company: customer.companyName,
                email: customer.representativeEmail,
                contracts: customerContracts || []
            };
        });
    }

    return {customers};
};

const mapDispatcherToProps = {
    getAllContracts,
    fetchAllCustomer,
    cancelRequest
};

export default connect(mapStateToProps, mapDispatcherToProps)(FulfillmentCustomers);
